/**
 * Speaker highlight palettes for Narrator Mode.
 * Each entry is a { highlight, text } pair with WCAG AA contrast (≥4.5:1).
 */

export type SpeakerColorPair = {
  highlight: string;
  text: string;
};

export type SpeakerPaletteId = "pastel" | "colorBlind" | "highContrast";

export const SPEAKER_PALETTE_IDS: readonly SpeakerPaletteId[] = [
  "pastel",
  "colorBlind",
  "highContrast",
] as const;

export const SPEAKER_PALETTE_LABELS: Record<SpeakerPaletteId, string> = {
  pastel: "Pastel",
  colorBlind: "Color-blind safe",
  highContrast: "High contrast",
};

/** Soft pastel backgrounds + dark text (default). */
const PASTEL: SpeakerColorPair[] = [
  { highlight: "#F5D0C8", text: "#3D1F18" },
  { highlight: "#C8E0F0", text: "#1A3344" },
  { highlight: "#D4E8C8", text: "#243818" },
  { highlight: "#E8D4F0", text: "#3A2048" },
  { highlight: "#F0E4C0", text: "#3D3410" },
  { highlight: "#C8E8E0", text: "#1A3A34" },
  { highlight: "#F0D0E0", text: "#4A1A30" },
  { highlight: "#D8D8F0", text: "#242448" },
  { highlight: "#E0F0D0", text: "#2A3A18" },
  { highlight: "#F0DCC8", text: "#3D2A14" },
  { highlight: "#C8D8E8", text: "#1A2A3A" },
  { highlight: "#E8E0D0", text: "#3A3420" },
];

/** Okabe–Ito / Wong inspired — avoid red/green-only discrimination. */
const COLOR_BLIND: SpeakerColorPair[] = [
  { highlight: "#A6CEE3", text: "#0D2B3E" },
  { highlight: "#FDBF6F", text: "#3D2800" },
  { highlight: "#B2DF8A", text: "#1A3308" },
  { highlight: "#CAB2D6", text: "#2A1840" },
  { highlight: "#FFFF99", text: "#333300" },
  { highlight: "#FB9A99", text: "#4A1010" },
  { highlight: "#1F78B4", text: "#FFFFFF" },
  { highlight: "#FF7F00", text: "#1A0A00" },
  { highlight: "#33A02C", text: "#FFFFFF" },
  { highlight: "#6A3D9A", text: "#FFFFFF" },
  { highlight: "#B15928", text: "#FFFFFF" },
  { highlight: "#E31A1C", text: "#FFFFFF" },
];

/** Stronger fills for low-vision sessions. */
const HIGH_CONTRAST: SpeakerColorPair[] = [
  { highlight: "#FFE066", text: "#000000" },
  { highlight: "#74C0FC", text: "#000000" },
  { highlight: "#FF8787", text: "#000000" },
  { highlight: "#69DB7C", text: "#000000" },
  { highlight: "#DA77F2", text: "#000000" },
  { highlight: "#FF922B", text: "#000000" },
  { highlight: "#4DABF7", text: "#000000" },
  { highlight: "#FF6B6B", text: "#000000" },
  { highlight: "#38D9A9", text: "#000000" },
  { highlight: "#F783AC", text: "#000000" },
  { highlight: "#FFD43B", text: "#000000" },
  { highlight: "#A9E34B", text: "#000000" },
];

export const SPEAKER_PALETTES: Record<SpeakerPaletteId, SpeakerColorPair[]> = {
  pastel: PASTEL,
  colorBlind: COLOR_BLIND,
  highContrast: HIGH_CONTRAST,
};

/** Reserved narrator (3rd-person / non-dialogue) colors per palette. */
export const NARRATOR_COLORS: Record<SpeakerPaletteId, SpeakerColorPair> = {
  pastel: { highlight: "#EDE8E0", text: "#2C2820" },
  colorBlind: { highlight: "#E8E8E8", text: "#1A1A1A" },
  highContrast: { highlight: "#FFFFFF", text: "#000000" },
};

export function parseSpeakerPaletteId(value: unknown): SpeakerPaletteId {
  if (value === "colorBlind" || value === "highContrast" || value === "pastel") return value;
  return "pastel";
}

export function getPaletteColors(paletteId: SpeakerPaletteId): SpeakerColorPair[] {
  return SPEAKER_PALETTES[paletteId] ?? PASTEL;
}

export function getNarratorColor(paletteId: SpeakerPaletteId): SpeakerColorPair {
  return NARRATOR_COLORS[paletteId] ?? NARRATOR_COLORS.pastel;
}

/** Stable color index from character id so reshuffles keep colors when possible. */
export function colorIndexForId(id: string, paletteLength: number): number {
  if (paletteLength <= 0) return 0;
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return hash % paletteLength;
}

export function assignSpeakerColor(
  characterId: string,
  paletteId: SpeakerPaletteId,
  usedIndexes?: Set<number>
): SpeakerColorPair {
  const colors = getPaletteColors(paletteId);
  let index = colorIndexForId(characterId, colors.length);
  if (usedIndexes && usedIndexes.size < colors.length) {
    let attempts = 0;
    while (usedIndexes.has(index) && attempts < colors.length) {
      index = (index + 1) % colors.length;
      attempts++;
    }
    usedIndexes.add(index);
  }
  return colors[index] ?? PASTEL[0]!;
}

export function normalizeSpeakerColorPair(raw: unknown): SpeakerColorPair | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const rec = raw as Record<string, unknown>;
  const highlight = typeof rec.highlight === "string" ? rec.highlight.trim() : "";
  const text = typeof rec.text === "string" ? rec.text.trim() : "";
  if (!highlight || !text) return undefined;
  return { highlight, text };
}
